import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
    DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { TableDescription, Metric, AggregationType } from '@/types';
import apiService from '@/services/api';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

interface MetricDefinitionModalProps {
    dbConnectionId: string;
    onMetricAdded: () => void;
}

const MetricDefinitionModal: React.FC<MetricDefinitionModalProps> = ({
    dbConnectionId, onMetricAdded
}) => {
    const [open, setOpen] = useState(false);
    const [tables, setTables] = useState<TableDescription[]>([]);
    // const [loading, setLoading] = useState(false);

    // Form State
    const [name, setName] = useState('');
    const [selectedTable, setSelectedTable] = useState('');
    const [selectedColumn, setSelectedColumn] = useState('');
    const [aggregation, setAggregation] = useState<AggregationType>('SUM');

    useEffect(() => {
        if (open) {
            loadTables();
        }
    }, [open]);

    const loadTables = async () => {
        try {
            // setLoading(true);
            const data = await apiService.getTableDescriptions(dbConnectionId);
            setTables(data);
        } catch (e) {
            // Silent error or toast
        }
    };

    const currentTable = tables.find(t => t.table_name === selectedTable);

    const handleSave = async () => {
        if (!name || !selectedTable || !selectedColumn) {
            toast.error('Please fill all fields');
            return;
        }

        try {
            const metric: Metric = {
                name,
                table_name: selectedTable,
                column_name: selectedColumn,
                aggregation,
                description: `Generated metric: ${aggregation}(${selectedColumn})`
            };

            await apiService.addMetric(dbConnectionId, metric);
            toast.success('Metric added successfully');
            setOpen(false);
            onMetricAdded();

            // Reset form
            setName('');
            setSelectedTable('');
            setSelectedColumn('');
        } catch (e: any) {
            toast.error(e.response?.data?.detail || 'Failed to add metric');
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-4 w-4 p-0">
                    <Plus className="h-4 w-4" />
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Define New Metric</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label>Metric Name</Label>
                        <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Total Revenue" />
                    </div>

                    <div className="space-y-2">
                        <Label>Table</Label>
                        <Select value={selectedTable} onValueChange={setSelectedTable}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select Table" />
                            </SelectTrigger>
                            <SelectContent style={{ maxHeight: '200px' }}>
                                {tables.map(t => (
                                    <SelectItem key={t.id} value={t.table_name}>{t.table_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label>Column</Label>
                        <Select value={selectedColumn} onValueChange={setSelectedColumn} disabled={!selectedTable}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select Column" />
                            </SelectTrigger>
                            <SelectContent style={{ maxHeight: '200px' }}>
                                {currentTable?.columns?.map(c => (
                                    <SelectItem key={c.name} value={c.name}>{c.name} ({c.data_type})</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label>Aggregation</Label>
                        <Select value={aggregation} onValueChange={(v) => setAggregation(v as AggregationType)}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="SUM">SUM</SelectItem>
                                <SelectItem value="AVG">AVG</SelectItem>
                                <SelectItem value="COUNT">COUNT</SelectItem>
                                <SelectItem value="MIN">MIN</SelectItem>
                                <SelectItem value="MAX">MAX</SelectItem>
                                <SelectItem value="COUNT_DISTINCT">COUNT DISTINCT</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={handleSave}>Save Metric</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default MetricDefinitionModal;
